
import Vue from 'vue';
import _chunk from 'lodash/chunk';
import { mapActions } from 'vuex';
import { functions } from '@/firebase';
import { SNACKBAR_STATUS } from '@/constants/constants';
import BaseButton from '@/components/base/BaseButton.vue';
import {
	ALPHA_BATCH,
	ALPHA_MAX_EMAILS,
	ALPHA_MAX_FILE_BYTES,
	AlphaMembership,
	AlphaReviewRow,
	AlphaRowGroup,
	alphaRowGroup,
	AlphaRowStatus,
	BoardsV2AlphaMatch,
	groupMemberships,
	leftOutNote,
	readCsvEmails,
	rowFromMatch,
} from './alphaCsv';

type Filter = 'all' | AlphaRowGroup;
type Counts = Record<AlphaRowGroup, number> & { all: number };
// The callables' answers; `message` is shown as is
interface LookupResult { success: boolean, message?: string, matches?: BoardsV2AlphaMatch[] }
interface SetResult { success: boolean, message?: string, changed?: number }

const STATUS_LABEL: Record<AlphaRowStatus, string> = {
	'checking': 'Checking',
	'error': 'Not checked',
	'ready': 'In the system',
	'in-alpha': 'Already in',
	'admin': 'Admin',
	'added': 'Added',
	'missing': 'Not found',
	'deleted': 'Deleted',
	'invalid': 'Invalid',
};
const STATUS_ICON: Record<AlphaRowStatus, string> = {
	'checking': '',
	'error': 'mdi-help',
	'ready': 'mdi-check',
	'in-alpha': 'mdi-check-all',
	'admin': 'mdi-shield-account-outline',
	'added': 'mdi-check-all',
	'missing': 'mdi-account-question-outline',
	'deleted': 'mdi-account-cancel-outline',
	'invalid': 'mdi-email-alert-outline',
};

async function call<T> (name: string, data: unknown): Promise<T> {
	const result = await functions.httpsCallable(name)(data);
	return result.data as T;
}

/**
 * The Boards v2 alpha's CSV upload, on the Boards v2 Rollout page (IL-107).
 * Reads every email in a file, checks each against RunDiffusion's accounts
 * (lookupBoardsV2AlphaEmails), and shows a row per email: in the system (with
 * its teams and companies), already in, or not in the system. The admin
 * removes rows, one at a time or every one not in the system at once, then
 * adds the accounts left (setBoardsV2Alpha). Rows not in the system are never
 * sent.
 */
export default Vue.extend({
	name: 'AlphaCsvReview',
	data () {
		return {
			rows: [] as AlphaReviewRow[],
			fileName: '',
			// emails in the file, as read (rows removed since don't change it)
			fileCount: 0,
			repeats: 0,
			filter: 'all' as Filter,
			dragOver: false,
			reading: false,
			checking: false,
			checkError: '',
			// a check still answering for a list since replaced is dropped
			runId: 0,
			adding: false,
			done: false,
			addedCount: 0,
		};
	},
	computed: {
		counts (): Counts {
			const counts: Counts = { all: this.rows.length, 'ready': 0, 'has-it': 0, 'not-found': 0, 'pending': 0 };
			this.rows.forEach((row: AlphaReviewRow) => {
				counts[alphaRowGroup(row.status)]++;
			});
			return counts;
		},
		checkedCount (): number {
			return this.rows.filter((row: AlphaReviewRow) => row.status !== 'checking').length;
		},
		checkProgress (): number {
			return this.rows.length ? (100 * this.checkedCount) / this.rows.length : 0;
		},
		filters (): Array<{ key: Filter, label: string, count: number }> {
			const filters: Array<{ key: Filter, label: string, count: number }> = [
				{ key: 'all', label: 'All', count: this.counts.all },
				{ key: 'ready', label: 'In the system', count: this.counts.ready },
				{ key: 'has-it', label: 'Already in', count: this.counts['has-it'] },
				{ key: 'not-found', label: 'Not in the system', count: this.counts['not-found'] },
			];
			if (this.counts.pending && !this.checking) filters.push({ key: 'pending', label: 'Not checked', count: this.counts.pending });
			return filters;
		},
		visibleRows (): AlphaReviewRow[] {
			if (this.filter === 'all') return this.rows;
			return this.rows.filter((row: AlphaReviewRow) => alphaRowGroup(row.status) === this.filter);
		},
		fileDetail (): string {
			const parts: string[] = [`${this.fileCount} ${this.fileCount === 1 ? 'email' : 'emails'} in the file`];
			if (this.repeats) parts.push(`${this.repeats} ${this.repeats === 1 ? 'repeat' : 'repeats'} merged`);
			return parts.join(' · ');
		},
		footNote (): string {
			if (this.done) {
				const people: string = `${this.addedCount} ${this.addedCount === 1 ? 'person' : 'people'}`;
				return `Added ${people}. They have Boards v2 now, on pages already open too.`;
			}
			if (this.checking) return 'Adding opens once every email is checked.';
			return leftOutNote([
				{ count: this.counts['not-found'], label: 'not in the system' },
				{ count: this.counts['has-it'], label: 'already in' },
				{ count: this.counts.pending, label: 'not checked' },
			]) || 'Every email here is an account, ready to add.';
		},
	},
	watch: {
		// a filter emptied by removing its last row goes back to all
		counts (counts: Counts) {
			if (this.filter !== 'all' && !counts[this.filter]) this.filter = 'all';
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		groupOf (row: AlphaReviewRow): AlphaRowGroup {
			return alphaRowGroup(row.status);
		},
		statusLabel (status: AlphaRowStatus): string {
			return STATUS_LABEL[status];
		},
		statusIcon (status: AlphaRowStatus): string {
			return STATUS_ICON[status];
		},
		statusDetail (row: AlphaReviewRow): string {
			switch (row.status) {
			case 'checking': return 'Checking...';
			case 'error': return 'Couldn\'t be checked';
			case 'admin': return 'RunDiffusion admin, who has Boards v2 already';
			case 'missing': return 'No RunDiffusion account has this email';
			case 'deleted': return 'This account was deleted';
			case 'invalid': return 'Not an email address';
			default: return 'Personal account, on no team';
			}
		},
		memberships (row: AlphaReviewRow): AlphaMembership[] {
			return groupMemberships(row.teams);
		},
		pickFile () {
			if (this.reading) return;
			const input = this.$refs.file as HTMLInputElement;
			input.value = '';
			input.click();
		},
		onFilePicked (e: Event) {
			const file: File | undefined = ((e.target as HTMLInputElement).files || [])[0];
			if (file) this.load(file);
		},
		onDrop (e: DragEvent) {
			this.dragOver = false;
			const file: File | undefined = (e.dataTransfer && e.dataTransfer.files[0]) || undefined;
			if (file) this.load(file);
		},
		fail (message: string) {
			this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message, show: true });
		},
		async load (file: File) {
			if (file.size > ALPHA_MAX_FILE_BYTES) {
				this.fail(`${file.name} is too large. Choose a CSV under 5 MB.`);
				return;
			}
			this.reading = true;
			let text: string = '';
			try {
				text = await file.text();
			} catch (e) {
				console.error('[boards v2 alpha] the file could not be read', e);
				this.fail(`${file.name} couldn't be read`);
				return;
			} finally {
				this.reading = false;
			}
			const { emails, invalid, repeats } = readCsvEmails(text);
			if (!emails.length && !invalid.length) {
				this.fail(`No emails in ${file.name}`);
				return;
			}
			if (emails.length + invalid.length > ALPHA_MAX_EMAILS) {
				this.fail(`${file.name} has ${emails.length + invalid.length} emails. Add at most ${ALPHA_MAX_EMAILS} at a time.`);
				return;
			}
			this.fileName = file.name;
			this.fileCount = emails.length + invalid.length;
			this.repeats = repeats;
			this.filter = 'all';
			this.done = false;
			this.addedCount = 0;
			this.rows = [
				...emails.map((email: string): AlphaReviewRow => ({ email, status: 'checking', userId: null, teams: [] })),
				...invalid.map((email: string): AlphaReviewRow => ({ email, status: 'invalid', userId: null, teams: [] })),
			];
			this.check(emails);
		},
		// Checks emails a batch at a time; a batch that fails is left to check again
		async check (emails: string[]) {
			const run: number = ++this.runId;
			this.checking = true;
			this.checkError = '';
			for (const part of _chunk(emails, ALPHA_BATCH)) {
				let matches: Record<string, BoardsV2AlphaMatch> | null = null;
				try {
					const result: LookupResult = await call<LookupResult>('lookupBoardsV2AlphaEmails', { emails: part });
					if (!result.success || !result.matches) throw new Error(result.message);
					matches = result.matches.reduce((acc: Record<string, BoardsV2AlphaMatch>, m: BoardsV2AlphaMatch) => ({ ...acc, [m.email]: m }), {});
				} catch (e) {
					console.error('[boards v2 alpha] a lookup failed', e);
					this.checkError = (e as Error).message || '';
				}
				if (run !== this.runId) return;
				const inPart: Set<string> = new Set(part);
				const found: Record<string, BoardsV2AlphaMatch> | null = matches;
				this.rows = this.rows.map((row: AlphaReviewRow): AlphaReviewRow => {
					if (!inPart.has(row.email)) return row;
					if (found && found[row.email]) return rowFromMatch(found[row.email]);
					return { ...row, status: 'error' };
				});
			}
			this.checking = false;
		},
		checkAgain () {
			const emails: string[] = this.rows.filter((row: AlphaReviewRow) => row.status === 'error').map((row: AlphaReviewRow) => row.email);
			this.rows = this.rows.map((row: AlphaReviewRow): AlphaReviewRow => (row.status === 'error' ? { ...row, status: 'checking' } : row));
			this.check(emails);
		},
		remove (row: AlphaReviewRow) {
			this.rows = this.rows.filter((r: AlphaReviewRow) => r.email !== row.email);
			if (!this.rows.length) this.reset();
		},
		removeNotFound () {
			const n: number = this.counts['not-found'];
			this.rows = this.rows.filter((row: AlphaReviewRow) => alphaRowGroup(row.status) !== 'not-found');
			this.updateSnackbar({
				status: SNACKBAR_STATUS.INFO,
				message: `Removed ${n} not in the system`,
				show: true,
			});
			if (!this.rows.length) this.reset();
		},
		reset () {
			this.runId++;
			this.rows = [];
			this.fileName = '';
			this.fileCount = 0;
			this.repeats = 0;
			this.filter = 'all';
			this.checking = false;
			this.checkError = '';
			this.done = false;
			this.addedCount = 0;
		},
		async add () {
			const ready: AlphaReviewRow[] = this.rows.filter((row: AlphaReviewRow) => row.status === 'ready');
			if (!ready.length) return;
			this.adding = true;
			let added: number = 0;
			try {
				for (const part of _chunk(ready, ALPHA_BATCH)) {
					const result: SetResult = await call<SetResult>('setBoardsV2Alpha', {
						userIds: part.map((row: AlphaReviewRow) => row.userId),
						on: true,
					});
					if (!result.success) throw new Error(result.message);
					const done: Set<string> = new Set(part.map((row: AlphaReviewRow) => row.email));
					this.rows = this.rows.map((row: AlphaReviewRow): AlphaReviewRow => (done.has(row.email) ? { ...row, status: 'added' } : row));
					added += part.length;
				}
				this.addedCount = added;
				this.done = true;
				this.filter = 'all';
				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: `Added ${added} ${added === 1 ? 'person' : 'people'} to the Boards v2 alpha`,
					show: true,
				});
			} catch (e) {
				console.error('[boards v2 alpha] adding failed', e);
				this.fail(added
					? `Added ${added}, then stopped: ${(e as Error).message || 'try again'}`
					: `Couldn't add them: ${(e as Error).message || 'try again'}`);
			} finally {
				this.adding = false;
			}
		},
	},
	components: {
		BaseButton,
	},
});
